import { MOCK_STAFF_USERS, PROTECTED_STAFF_USER_IDS } from "@/mocks/users";
import type { Role, StaffUser, UserDraft } from "@/services/types";
import { ApiError } from "@/services/types";
import { USE_MOCK_API } from "@/services/config";
import { http } from "@/services/http";

/**
 * Centralized staff-user service. Users are persisted to PostgreSQL `app_users`.
 * Users are never deleted — only deactivated.
 */

const delay = (ms = 350) => new Promise((resolve) => setTimeout(resolve, ms));

let store: StaffUser[] = reseed();

function reseed(): StaffUser[] {
  return JSON.parse(JSON.stringify(MOCK_STAFF_USERS)) as StaffUser[];
}

export function isProtectedUser(user: StaffUser): boolean {
  return USE_MOCK_API && PROTECTED_STAFF_USER_IDS.includes(user.id);
}

export async function listUsers(): Promise<StaffUser[]> {
  if (!USE_MOCK_API) {
    try {
      return await http<StaffUser[]>("/users");
    } catch {
      // Fallback
    }
  }
  await delay();
  return JSON.parse(JSON.stringify(store));
}

export async function getUser(id: string): Promise<StaffUser> {
  if (!USE_MOCK_API) {
    try {
      return await http<StaffUser>(`/users/${id}`);
    } catch {
      // Fallback
    }
  }
  await delay(80);
  const user = store.find((u) => u.id === id);
  if (!user) throw new ApiError("User not found.", 404, "USER_NOT_FOUND");
  return { ...user };
}

export async function createUser(draft: UserDraft, actor: string): Promise<StaffUser> {
  if (!USE_MOCK_API) {
    try {
      return await http<StaffUser>("/users", {
        method: "POST",
        body: {
          name: draft.name,
          email: draft.email,
          phone: draft.phone,
          role: draft.role.toUpperCase(),
          status: draft.status,
          password: draft.password,
        },
      });
    } catch (e) {
      if (e instanceof ApiError) throw e;
    }
  }
  await delay(400);
  const email = draft.email.trim().toLowerCase();
  if (store.some((u) => u.email.toLowerCase() === email)) {
    throw new ApiError("A user with this email already exists.", 409, "DUPLICATE_EMAIL");
  }
  const now = new Date().toISOString();
  const user: StaffUser = {
    id: `u-${Date.now()}`,
    name: draft.name.trim(),
    email,
    phone: draft.phone.trim(),
    role: draft.role,
    status: draft.status,
    avatarColor: "#4f46e5",
    lastLogin: null,
    createdAt: now,
  };
  store = [user, ...store];
  return { ...user };
}

export async function updateUser(
  id: string,
  draft: Pick<UserDraft, "name" | "email" | "phone" | "role"> & { password?: string },
  actor: string,
  currentUserId: string
): Promise<StaffUser> {
  if (!USE_MOCK_API) {
    try {
      return await http<StaffUser>(`/users/${id}`, {
        method: "PUT",
        body: {
          name: draft.name,
          email: draft.email,
          phone: draft.phone,
          role: draft.role.toUpperCase(),
          password: draft.password,
        },
      });
    } catch (e) {
      if (e instanceof ApiError) throw e;
    }
  }
  await delay(400);
  const index = store.findIndex((u) => u.id === id);
  if (index === -1) throw new ApiError("User not found.", 404, "USER_NOT_FOUND");
  const existing = store[index];

  const email = draft.email.trim().toLowerCase();
  const dup = store.find((u) => u.email.toLowerCase() === email && u.id !== id);
  if (dup) throw new ApiError("A user with this email already exists.", 409, "DUPLICATE_EMAIL");

  if (id === currentUserId && draft.role !== existing.role) {
    throw new ApiError("You cannot change your own role.", 400, "SELF_DEMOTION");
  }

  const updated: StaffUser = {
    ...existing,
    name: draft.name.trim(),
    email,
    phone: draft.phone.trim(),
    role: draft.role,
  };
  store = store.map((u) => (u.id === id ? updated : u));
  return { ...updated };
}

export async function setUserStatus(
  id: string,
  status: "active" | "inactive",
  actor: string,
  currentUserId: string
): Promise<StaffUser> {
  if (!USE_MOCK_API) {
    try {
      return await http<StaffUser>(`/users/${id}/status`, {
        method: "PATCH",
        body: { status },
      });
    } catch (e) {
      if (e instanceof ApiError) throw e;
    }
  }
  await delay(400);
  const index = store.findIndex((u) => u.id === id);
  if (index === -1) throw new ApiError("User not found.", 404, "USER_NOT_FOUND");
  const existing = store[index];

  if (id === currentUserId && status === "inactive") {
    throw new ApiError("You cannot deactivate your own account.", 400, "SELF_DEACTIVATE");
  }

  const updated: StaffUser = { ...existing, status };
  store = store.map((u) => (u.id === id ? updated : u));
  return { ...updated };
}

export async function resetUserPassword(
  id: string,
  actor: string,
  customPassword?: string
): Promise<{ userId: string; temporaryPassword: string }> {
  if (!USE_MOCK_API) {
    try {
      return await http<{ userId: string; temporaryPassword: string }>(`/users/${id}/reset-password`, {
        method: "POST",
        body: customPassword ? { password: customPassword } : {},
      });
    } catch (e) {
      if (e instanceof ApiError) throw e;
    }
  }
  await delay(400);
  const user = store.find((u) => u.id === id);
  if (!user) throw new ApiError("User not found.", 404, "USER_NOT_FOUND");
  const temporaryPassword = customPassword?.trim() || "TempPassword123!";
  return { userId: id, temporaryPassword };
}

export async function deleteUser(id: string): Promise<void> {
  if (!USE_MOCK_API) {
    try {
      await http<void>(`/users/${id}`, {
        method: "DELETE",
      });
      return;
    } catch (e) {
      if (e instanceof ApiError) throw e;
    }
  }
  await delay(400);
  store = store.filter((u) => u.id !== id);
}

export function canModifyUser(user: StaffUser, actorRole: Role, currentUserId: string): boolean {
  if (actorRole !== "super_admin") return false;
  if (user.id === currentUserId) return false;
  return true;
}

export const usersService = {
  listUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  setUserStatus,
  resetUserPassword,
  canModifyUser,
  isProtectedUser,
};